import { useCallback } from 'react'
import type {
  DatabaseInvoice,
  ColumnDefinition,
  InvoiceInput,
  InvoiceStatus,
  PaginatedResponse,
  DatabaseFilterParams,
  DatabaseStats,
} from '@/types/database'

const API_URL = import.meta.env.VITE_API_URL || '/v1'

export function useDatabase() {
  const loading = false
  const error = null

  // Helper function for API calls
  const apiCall = useCallback(async <T>(
    endpoint: string,
    options?: RequestInit
  ): Promise<T> => {
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }))
      throw new Error(error.error || error.message || 'Request failed')
    }

    if (response.status === 204) {
      return undefined as T
    }

    const text = await response.text()
    if (!text) {
      return undefined as T
    }

    return JSON.parse(text) as T
  }, [])

  // Get all invoices with pagination
  const getInvoices = useCallback(
    async (params: {
      page?: number
      limit?: number
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
      filters?: DatabaseFilterParams
    } = {}
  ): Promise<PaginatedResponse<DatabaseInvoice>> => {
      const queryParams = new URLSearchParams()
      if (params.page) queryParams.append('page', String(params.page))
      if (params.limit) queryParams.append('limit', String(params.limit))
      if (params.sortBy) queryParams.append('sortBy', params.sortBy)
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder)
      if (params.filters?.supplierName) queryParams.append('supplierName', params.filters.supplierName)
      if (params.filters?.dateFrom) queryParams.append('dateFrom', params.filters.dateFrom)
      if (params.filters?.dateTo) queryParams.append('dateTo', params.filters.dateTo)
      if (params.filters?.source) queryParams.append('source', params.filters.source)

      return apiCall<PaginatedResponse<DatabaseInvoice>>(
        `/database/invoices?${queryParams.toString()}`
      )
    },
    [apiCall]
  )

  // Get single invoice
  const getInvoiceById = useCallback(
    async (id: string): Promise<DatabaseInvoice> => {
      return apiCall<DatabaseInvoice>(`/database/invoices/${id}`)
    },
    [apiCall]
  )

  // Create new invoice
  const createInvoice = useCallback(
    async (input: InvoiceInput): Promise<DatabaseInvoice> => {
      return apiCall<DatabaseInvoice>('/database/invoices', {
        method: 'POST',
        body: JSON.stringify(input),
      })
    },
    [apiCall]
  )

  // Create multiple invoices
  const createInvoices = useCallback(
    async (inputs: InvoiceInput[]): Promise<DatabaseInvoice[]> => {
      return apiCall<DatabaseInvoice[]>('/database/invoices/batch', {
        method: 'POST',
        body: JSON.stringify(inputs),
      })
    },
    [apiCall]
  )

  // Update existing invoice
  const updateInvoice = useCallback(
    async (id: string, input: Partial<InvoiceInput>): Promise<DatabaseInvoice> => {
      return apiCall<DatabaseInvoice>(`/database/invoices/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      })
    },
    [apiCall]
  )

  // Bulk-update status (+ optional approver/approvalDate) for many invoices
  const bulkUpdateStatus = useCallback(
    async (
      ids: string[],
      status: InvoiceStatus,
      approver?: string | null,
      approvalDate?: string | null,
    ): Promise<{ updated: number }> => {
      return apiCall<{ updated: number }>('/database/invoices/bulk-status', {
        method: 'POST',
        body: JSON.stringify({ ids, status, approver, approvalDate }),
      })
    },
    [apiCall]
  )

  // Delete invoice
  const deleteInvoice = useCallback(
    async (id: string): Promise<void> => {
      await apiCall<void>(`/database/invoices/${id}`, { method: 'DELETE' })
    },
    [apiCall]
  )

  // Delete all invoices
  const deleteAllInvoices = useCallback(async (): Promise<number> => {
    const result = await apiCall<{ deleted: number }>('/database/invoices', {
      method: 'DELETE',
    })
    return result.deleted
  }, [apiCall])

  // Get database statistics
  const getStats = useCallback(async (): Promise<DatabaseStats> => {
    return apiCall<DatabaseStats>('/database/stats')
  }, [apiCall])

  // Get unique suppliers
  const getUniqueSuppliers = useCallback(async (): Promise<string[]> => {
    const result = await apiCall<{ suppliers: string[] }>('/database/suppliers')
    return result.suppliers
  }, [apiCall])

  // Check for duplicate invoices
  const checkDuplicate = useCallback(
    async (payload: {
      supplierName: string
      rechnungsnummer: string
      documentType?: string
    }): Promise<{
      isDuplicate: boolean
      duplicateCount: number
      duplicateIds: string[]
    }> => {
      return apiCall<{
        isDuplicate: boolean
        duplicateCount: number
        duplicateIds: string[]
      }>('/database/invoices/check-duplicate', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    },
    [apiCall]
  )

  // Get all custom columns
  const getColumns = useCallback(async (): Promise<ColumnDefinition[]> => {
    const result = await apiCall<{ columns: ColumnDefinition[] }>('/database/columns')
    return result.columns
  }, [apiCall])

  // Create custom column
  const createColumn = useCallback(
    async (name: string, type: 'text' | 'number' | 'date'): Promise<ColumnDefinition> => {
      return apiCall<ColumnDefinition>('/database/columns', {
        method: 'POST',
        body: JSON.stringify({ name, type }),
      })
    },
    [apiCall]
  )

  // Delete custom column
  const deleteColumn = useCallback(
    async (id: string): Promise<void> => {
      await apiCall<void>(`/database/columns/${id}`, { method: 'DELETE' })
    },
    [apiCall]
  )

  // Export invoices
  const exportInvoices = useCallback(
    async (config: { format: 'xlsx' | 'csv'; separator: 'comma' | 'period'; includeCustomColumns: boolean }): Promise<void> => {
      const queryParams = new URLSearchParams()
      queryParams.append('format', config.format)
      queryParams.append('separator', config.separator)
      if (!config.includeCustomColumns) {
        queryParams.append('includeCustomColumns', 'false')
      }

      const response = await fetch(`${API_URL}/database/export?${queryParams.toString()}`, {
        credentials: 'include',
      })
      if (!response.ok) {
        throw new Error('Export failed')
      }

      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = `invoices.${config.format}`
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+)"?/)
        if (match) filename = match[1]
      }

      // Download the file
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    },
    []
  )

  // Import invoices from Excel
  const importFromExcel = useCallback(
    async (file: File): Promise<{ imported: number; invoices: DatabaseInvoice[] }> => {
      console.log('importFromExcel called with file:', file.name, 'size:', file.size, 'API_URL:', API_URL)
      const formData = new FormData()
      formData.append('file', file)

      console.log('Calling fetch to:', `${API_URL}/database/import/excel`)
      const response = await fetch(`${API_URL}/database/import/excel`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Import failed' }))
        throw new Error(error.error || 'Import failed')
      }

      return response.json()
    },
    []
  )

  return {
    loading,
    error,
    getInvoices,
    getInvoiceById,
    createInvoice,
    createInvoices,
    updateInvoice,
    bulkUpdateStatus,
    deleteInvoice,
    deleteAllInvoices,
    getStats,
    getUniqueSuppliers,
    checkDuplicate,
    getColumns,
    createColumn,
    deleteColumn,
    exportInvoices,
    importFromExcel,
  }
}
